import React from "react";
import { Check, Zap, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const FEATURES = [
  "Claude Sonnet 5 AI signals (BUY / SELL / HOLD)",
  "Mean-reversion scalping bot (dip-buy / rip-sell)",
  "Hands-off auto take-profit & stop-loss",
  "Alpaca broker connect (paper & live)",
  "Custom parameters: risk, size, confidence, TP/SL",
  "Live watchlist, chart & trade history",
];

export default function Pricing({ onCta }) {
  return (
    <section id="pricing" className="relative z-10 px-6 lg:px-12 py-20 max-w-6xl mx-auto">
      <div className="text-center max-w-2xl mx-auto">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">Pricing</div>
        <h2 className="font-display text-3xl lg:text-5xl font-extrabold mt-3 tracking-tight">
          Free to join. <span className="text-emerald-400">Pay only when you win.</span>
        </h2>
        <p className="mt-4 text-slate-400 text-sm sm:text-base leading-relaxed">
          No subscription. No hidden charges. TradingoX takes a flat <b className="text-emerald-300">1.25% fee on each
          individual trade&rsquo;s realized profit</b> — nothing on losses, nothing on break-even trades.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-5">
        <div className="aq-card p-6">
          <div className="w-10 h-10 rounded-lg bg-white/5 grid place-items-center"><Zap className="w-5 h-5 text-emerald-300"/></div>
          <div className="mt-4 font-display text-2xl font-extrabold">$0</div>
          <div className="text-slate-400 text-sm mt-1">To sign up & use the platform</div>
          <ul className="mt-5 space-y-2.5">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                <Check className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="aq-card p-6 md:col-span-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">
            <ShieldCheck className="w-3 h-3" /> How the 1.25% fee works
          </div>
          <div className="mt-3 grid sm:grid-cols-3 gap-4">
            <FeeStep n="01" title="You close a trade">
              Bot or manual — the SELL order settles at your broker.
            </FeeStep>
            <FeeStep n="02" title="Only if profitable">
              We compute realized P&L per trade. Loss or flat? Fee is <b>$0</b>.
            </FeeStep>
            <FeeStep n="03" title="1.25% is accrued">
              Balance shows in your dashboard. Pay it via Stripe whenever you like.
            </FeeStep>
          </div>
          <div className="mt-5 rounded-lg bg-black/40 border border-white/5 p-4 text-sm text-slate-300">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Example</div>
            You buy AAPL at $180 and sell at $182 for 10 shares → <b className="text-emerald-300">$20 profit</b>.
            TradingoX fee = $20 × 1.25% = <b className="text-emerald-300">$0.25</b>. You keep $19.75.
          </div>
          <div className="mt-5 text-xs text-slate-400 leading-relaxed">
            Your broker still holds all your funds. The fee is settled separately via Stripe — TradingoX never touches your trading balance.
          </div>
          {onCta && (
            <Button onClick={onCta} className="mt-6 rounded-full h-11 px-6 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              Start trading free
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function FeeStep({ n, title, children }) {
  return (
    <div className="rounded-lg bg-black/30 border border-white/5 p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">{n}</div>
      <div className="mt-1 font-display text-base font-bold">{title}</div>
      <p className="mt-1 text-xs text-slate-400 leading-relaxed">{children}</p>
    </div>
  );
}
